import { useEffect } from 'react';

import { useUser } from '@/components/auth-provider';
import { useBusinessStore } from '@/stores/business-store';
import type { Membership } from '@/types';

/**
 * Resolves the full `repo.Membership` (business_id, business_name, role,
 * permissions) for whichever business the signed-in user is currently
 * acting as. Backs Tasks 6/7, which need a `business_id` to scope
 * `/businesses/{business_id}/...` calls.
 *
 * Resolution order:
 * 1. The persisted `activeBusinessId` (see `business-store.ts`), if it
 *    still matches one of the user's current memberships.
 * 2. Otherwise the first membership in `user.businesses` — this also
 *    covers the common single-business case (nothing to choose between)
 *    and self-heals a stale/removed selection (e.g. the user lost access
 *    to a previously active business).
 *
 * The resolved id is written back to the store so it survives a refresh
 * without waiting on a switcher UI to ever exist.
 */
export function useCurrentBusiness(): Membership | null {
  const { user } = useUser();
  const activeBusinessId = useBusinessStore((state) => state.activeBusinessId);
  const setActiveBusinessId = useBusinessStore((state) => state.setActiveBusinessId);

  const businesses = user?.businesses ?? [];
  const resolved =
    businesses.find((membership) => membership.business_id === activeBusinessId) ??
    businesses[0] ??
    null;

  useEffect(() => {
    if (resolved && resolved.business_id !== activeBusinessId) {
      setActiveBusinessId(resolved.business_id);
    }
  }, [resolved, activeBusinessId, setActiveBusinessId]);

  return resolved;
}
